import React, { useState, useEffect, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import CaseContextProblem from "./components/CaseContextProblem";
import QuestionSection from "./components/QuestionSection";
import Transcript from "./components/Transcript";
import ImprovedCandidateResponse from "./components/ImprovedCandidateResponse";
import {
  QuestionType,
  CaseQuestionData,
  CaseContext,
  CaseProblem,
  ImprovedResponseData,
} from "./types";
import { useFetchCases } from "src/hooks/useFetchCases";
import {
  useFetchImprovedResponses,
  ImprovedResponse,
} from "src/hooks/useFetchImprovedResponses";
export interface TranscriptEntry {
  role: "user" | "interviewer";
  statement: string;
  recordingUrl?: string;
  timestamp?: string;
}

interface DetailsTabProps {
  sessionId?: string;
  caseId?: number;
  caseContext: CaseContext;
  caseProblem: CaseProblem;
  questionData: Record<QuestionType, CaseQuestionData>;
  improvedResponses: Record<number, ImprovedResponse[]>;
  currentQuestionIndex: number;
  onQuestionChange: (index: number) => void;
  isLoading?: boolean;
  rawImprovedResponsesData?: ImprovedResponseData[] | null;
}

const DetailsTab: React.FC<DetailsTabProps> = ({
  sessionId,
  caseId,
  caseContext,
  caseProblem,
  questionData,
  improvedResponses,
  currentQuestionIndex,
  onQuestionChange,
  isLoading = false,
  rawImprovedResponsesData,
}) => {
  const [isContextExpanded, setIsContextExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState<QuestionType>(
    Object.keys(questionData).length > 0
      ? (Object.keys(questionData)[0] as QuestionType)
      : "opening_and_structure"
  );

  // Use the hook to fetch improved responses
  const {
    improvedResponsesData,
    isLoading: isLoadingImprovedResponses,
    isError,
    error,
  } = useFetchImprovedResponses(sessionId);

  // Use either the data from props (if available) or the fetched data
  const finalImprovedResponsesData =
    rawImprovedResponsesData || improvedResponsesData;
  const isLoadingData = isLoading || isLoadingImprovedResponses;

  // Place responses at their correct indices based on the last number in their IDs
  const indexedImprovedResponsesData = useMemo(() => {
    if (!finalImprovedResponsesData) return null;

    // Create a new array with the correct length
    const result = new Array(finalImprovedResponsesData.length);

    // Place each item at its correct index
    finalImprovedResponsesData.forEach((item) => {
      const index = parseInt(item.id.substr(item.id.lastIndexOf("-") + 1));
      result[index] = item;
    });

    return result;
  }, [finalImprovedResponsesData]);

  // Update question index when active section changes
  useEffect(() => {
    if (questionData && questionData[activeSection]) {
      onQuestionChange(questionData[activeSection].case_question_number);
    }
  }, [activeSection, questionData, onQuestionChange]);

  // Get current question data
  const currentQuestionData = questionData[activeSection] || {
    question: "",
    case_question_number: 1,
    availableInformation: [],
    graphs: [],
  };

  // Filter improved responses by active section
  const currentImprovedResponses =
    indexedImprovedResponsesData &&
    indexedImprovedResponsesData[currentQuestionIndex - 1]
      ? indexedImprovedResponsesData[currentQuestionIndex - 1]
      : null;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Case Context & Problem Section */}
      <div
        className={`bg-[#F5F6F7] border border-[#E6E8EB] rounded-lg ${
          isContextExpanded ? "pb-4 bg-white" : ""
        }`}
      >
        <button
          onClick={() => setIsContextExpanded(!isContextExpanded)}
          className="w-full flex items-center justify-between p-4"
        >
          <span className="text-lg font-semibold text-[#1F2024]">
            Case Context & Problem
          </span>
          {isContextExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#474A52]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#474A52]" />
          )}
        </button>
        {isContextExpanded && (
          <div className="px-4">
            <CaseContextProblem context={caseContext} problem={caseProblem} />
          </div>
        )}
      </div>

      {/* Question Section with Tabs */}
      <QuestionSection
        question={{
          text: currentQuestionData.question,
          number: currentQuestionData.case_question_number,
          availableInformation: Array.isArray(
            currentQuestionData.availableInformation
          )
            ? currentQuestionData.availableInformation
            : currentQuestionData.availableInformation?.split("\n"),
          graphs: currentQuestionData.graphs,
          questionType: currentQuestionData.questionType,
        }}
        onTabChange={setActiveSection}
        activeTab={activeSection}
        allQuestions={questionData}
        isLoading={isLoadingData}
      />

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-4">
        {/* Left Column - Transcript with embedded Recording */}
        <div className="col-span-1">
          {isLoadingData ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <Transcript
              sessionId={sessionId}
              currentQuestionIndex={currentQuestionIndex}
              key={`transcript-${activeSection}-${currentQuestionIndex}`}
            />
          )}
        </div>

        {/* Right Column - Improved Candidate Response */}
        <div className="col-span-2">
          {isLoadingData ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <ImprovedCandidateResponse
              answers={currentImprovedResponses}
              key={`improved-${activeSection}-${currentQuestionIndex}`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailsTab;
